import { AfterContentInit, Component, DestroyRef, HostListener, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { parseZone } from 'moment';
import { map } from 'rxjs';

import { GridColumn, GridColumnTypeEnum } from '../../../models/common/grid-column.models';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import { CompanyOrderListItem } from '../../../models/company/company-order-list-item.model';
import { CompanyService } from '../../../services/company.service';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { TopbarService } from '../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterDialogData } from '../../../shared/models/types/filter-dialog-data.type';
import { PhotographerFilterService } from '../../../shared/services/photographer-filter.service';
import { GraphQLService } from '../../../services/graph-ql.service';
import { OrderStatus } from '../../../models/common/order-status.models';
import { OrderStatusFilterService } from '../../../shared/services/order-status-filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from '../../../shared/components/filter-list/filter-list.component';
import { FilterService } from '../../../shared/services/filter.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { AppService } from '../../../app.service';
import { WrapperResultGraphQL } from '../../../models/shared/types/wrapper-result-graphql.type';
import { Photographer } from '../../../models/common/photographer.models';
import { Ids } from '../../../models/common/ids.model';

@Component({
  standalone: false,
  selector: 'bt-company-order-list',
  templateUrl: './company-order-list.component.html',
  styleUrls: ['./company-order-list.component.scss']
})

export class CompanyOrderListComponent implements AfterContentInit {

  readonly #destroyRef = inject(DestroyRef);
  readonly #graphQlService = inject(GraphQLService);
  readonly #dialog = inject(MatDialog);
  readonly #filterService = inject(FilterService);
  readonly #appService = inject(AppService);

  currentSidebar: SidebarTypeEnum;
  sidebarTypeEnum = SidebarTypeEnum;
  gridColumnTypeEnum = GridColumnTypeEnum;
  gridColumns: Array<GridColumn>;

  orders: { data: Array<CompanyOrderListItem>, page: number, search: string, size: number, total: number } = {
    size: 10,
    page: 0,
    search: '',
    data: [],
    total: 0
  };
  loaderId = UtilityFunctions.newGuid();

  filtersList = [
    { id: 'photographers', label: 'Photographers' },
    { id: 'orderStatuses', label: 'Order Status' }
  ];

  photographers: FilterDialogData<Photographer> = {
    title: 'Photographers',
    selected: [],
    filterService: inject(PhotographerFilterService),
    variables: { page: 0, size: 50 }
  };
  orderStatuses: FilterDialogData<OrderStatus> = {
    title: 'Order Status',
    selected: [],
    filterService: inject(OrderStatusFilterService)
  };

  datesFilters: { fromDateCached?: string, fromDateCurrent?: string, toDateCached?: string, toDateCurrent?: string, notSetDueDate?: boolean, notSetDueDateCurrent?: boolean } = {};

  orderId: number;
  orderNumber: string;
  modelId: number;

  constructor(
    private _activatedRoute: ActivatedRoute,
    // private companyService: CompanyService,
    // private commonService: CommonService,
    private topbarService: TopbarService,
    private sidenavService: SidenavService
  ) {
    this.queryParamsSubscribe();
    this.setGridColumns();
    this.searchTextSubscription();
  }

  ngAfterContentInit(): void {
    //Acquisizione dei parametri dell'url
    this.getData();
  }

  onCustomOrder() {
    this.currentSidebar = SidebarTypeEnum.customOrder;
  }

  onInfo(item: CompanyOrderListItem) {
    const params: Params = { orderId: item.orderId, orderNumber: item.orderNumber };
    this.sidenavService.newItemUrlPart('order-list', params);
  }

  onExitFromInfo() {
    const params: Params = {};
    this.sidenavService.newItemUrlPart('order-list', params);
  }

  onOpenFilters() {
    this.currentSidebar = SidebarTypeEnum.filters;
    // this.photoFilters.selectedCurrent = UtilityFunctions.DeepClone(this.photoFilters.selectedCached);
    // this.statusFilters.selectedCurrent = UtilityFunctions.DeepClone(this.statusFilters.selectedCached);
    this.datesFilters.fromDateCurrent = UtilityFunctions.DeepClone(this.datesFilters.fromDateCached);
    this.datesFilters.toDateCurrent = UtilityFunctions.DeepClone(this.datesFilters.toDateCached);
    this.datesFilters.notSetDueDateCurrent = this.datesFilters.notSetDueDate;
    //this.getPhotographers(0);
    //this.getOrderStatus();
  }

  openDialogFilter(title: string = ''): void {
    const initialValue = [...this[title].selected];
    const dialog = this.#dialog.open(FilterListComponent<any>, {
      data: { title: this[title].title, selected: this[title].selected, filterService: this[title].filterService, variables: this[title].variables },
      height: '70%',
      width: 'auto',
      minWidth: '25%'
    });

    dialog.afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.applyFilters(title, res)
          }
        }
      });
  }

  getLabel(filter: string): string {
    return this[filter].selected.length ? `: ${this[filter].selected.map(el => el.displayName).join(', ').slice(0, 20)}` : '';
  }

  getFullTitleFilter(filter: string): string {
    return this[filter].selected.length ? `${this[filter].selected.map(el => el.displayName).join(', ')}` : '';
  }

  clearFilter(event: any, filter: string): void {
    event.stopPropagation();
    this[filter].selected = [];
    (this[filter] as FilterDialogData<any>).filterService.clearSelection();
    this.#filterService.removeDataFromUrl(filter, () => null);
    if (filter === 'brands') this['categories'].variables.brandIds = [];
  }

  getData() {
    const params = this.#filterService.getDataFromUrl();
    this.getParams(params);
  }

  //Prende e assegna i parametri dell'url
  private getParams(params: { [key: string]: string }): void {
    if (params['search'])
      this.orders.search = params['search'];

    if (params['photographers']) {
      const ids = params['photographers'].split('-').map(el => new Ids(+el));
      this.getPhotographers(ids);
    } else {
      this.photographers.selected = [];
    }

    if (params['orderStatuses']) {
      const ids = params['orderStatuses'].split('-').map(el => +el);
      this.getOrderStatus(ids);
    } else {
      this.orderStatuses.selected = [];
    }
  }

  // inViewportAction(ev: any, p: Photographer) {
  //   p.isVisible = ev.isVisible;
  //   if (p.isVisible) {
  //     const index = this.photoFilters.data.findIndex(el => el.personId === p.personId);
  //     if (index === this.photoFilters.data.length - 1 && this.photoFilters.data.length / this.photoFilters.size === this.photoFilters.page + 1) {
  //       this.getPhotographers(this.photoFilters.page + 1);
  //     }
  //   }
  // }

  // onBlurSearchPhoto() {
  //   if (!this.photoFilters.search) {
  //     this.getPhotographers(0);
  //   }
  // }

  // onClearSearchPhoto() {
  //   this.photoFilters.search = '';
  //   this.getPhotographers(0);
  // }


  //Replace with get order status
  // getPhotographers(page: number) {
  //   this.photoFilters.page = page;
  //   this.companyService.getPhotographers(this.photoFilters.page, this.photoFilters.size, this.photoFilters.search, null, null, this.photoFilters.loaderId).subscribe(data => {
  //     data.forEach(p => p.checked = this.photoFilters.selectedCurrent.some(el => el.personId === p.personId));
  //     this.photoFilters.data = !this.photoFilters.page ? data : this.photoFilters.data.concat(data);
  //   });
  // }

  private getPhotographers(photographerIds: Array<Ids>) {
    const filters = {
      companyId: this.#appService.companyId,
      paginationPage: 0,
      paginationSize: 50,
      search: '',
      statusIds: [],
      photographerIds,
    };

    this.#graphQlService.getData<Photographer>(GetQuery.GET_PHOTOGRAPHERS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['photographers']))
      .subscribe((data: WrapperResultGraphQL<Photographer>) => {
        this['photographers'].selected = data.nodes.map(el => ({ ...el, displayId: el.personId!, displayName: el.fullName!, selected: true, disabled: false }));
      });
  }

  getOrderStatus(ids: Array<number>) {
    this.#graphQlService.getData<any>(GetQuery.GET_ORDER_STATUSES, { first: 50, after: null, search: '', ids })
      .pipe(
        map(res => res['data']['orderStatuses']),
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe({
        next: (data: WrapperResultGraphQL<any>) => {
          this.orderStatuses.selected = data.nodes.map(el => ({ statusId: el.id, statusName: el.name, displayId: el.id!, displayName: el.name, disabled: false, selected: true }))
        }
      });
  }

  applyFilters(filter: string, selected: Array<any>) {
    if (!selected.length) {
      // Rimozione il parametro dall'url
      this.#filterService.removeDataFromUrl(filter, () => null);
    } else {
      this[filter].selected = selected;
      const ids = selected.map(el => `${el.displayId}`).join('-');
      this.#filterService.setQueryParams({ [filter]: ids });
    }
  }

  onExitFilters() {
    this.currentSidebar = null;
    // this.photoFilters.data = null;
    // this.photoFilters.selectedCurrent = null;
    // this.photoFilters.search = null;
    // this.statusFilters.data = null;
    // this.statusFilters.selectedCurrent = null;
    this.datesFilters.fromDateCurrent = null;
    this.datesFilters.toDateCurrent = null;
  }

  getOrders(page: number) {
    this.orders.page = page;
    const params = this.#filterService.getDataFromUrl();
    
    const filters = {
      notSetDueDate: this.datesFilters.notSetDueDate ?? false,
      photographerIds: (params && params['photographers']) ? params['photographers'].split('-').map(el => new Ids(+el)) : [],
      fromDueDate: this.datesFilters.fromDateCached ? parseZone(this.datesFilters.fromDateCached).utc(true).toISOString() : null,
      toDueDate: this.datesFilters.toDateCached ? parseZone(this.datesFilters.toDateCached).utc(true).toISOString() : null,
      orderStatusIds: (params && params['orderStatuses']) ? params['orderStatuses'].split('-').map(el => new Ids(+el)) : []
    };
    const variables = {
      companyId: this.#appService.companyId,
      paginationPage: this.orders.page,
      paginationSize: this.orders.size,
      search: this.orders.search ?? '',
      filters
    }

    this.#graphQlService.getData<CompanyOrderListItem>(GetQuery.GET_COMPANY_ORDERS, variables)
      .pipe(
        map(res => res['data']['companyOrders']['nodes'][0]),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (data) => {
          this.orders.data = data.orders;
          this.orders.total = data.total;
          this.currentSidebar = null;
        }
      });
  }

  removeFromDateFilter() {
    this.datesFilters.fromDateCached = null;
    this.getOrders(0);
  }

  removeToDateFilter() {
    this.datesFilters.toDateCached = null;
    this.getOrders(0);
  }

  removeWithoutDueDateFilter() {
    this.datesFilters.notSetDueDate = false;
    this.getOrders(0);
  }

  createdCustomOrder() {
    this.getOrders(0);
  }

  errorCreationCustomOrder() {
    this.currentSidebar = null;
  }

  private searchTextSubscription() {
    this.topbarService.searchTextSubscribe
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        if (data !== this.orders.search && !this.orderId && !this.orderNumber) {
          this.orders.search = data;
          this.getOrders(0);
        }
      })

  }

  private setGridColumns() {
    this.gridColumns = [
      { title: '', field: '', type: GridColumnTypeEnum.icon },
      { title: 'Order #', field: 'orderNumber' },
      { title: 'Status', field: 'orderStatusName' },
      { title: 'Photographer', field: 'photographerFullName' },
      { title: 'Due Date', field: 'dueDate', type: GridColumnTypeEnum.date },
      { title: 'Shots', field: '', type: GridColumnTypeEnum.custom }
    ];
  }

  private queryParamsSubscribe() {
    this._activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        const orderId = data['orderId'];
        this.orderId = !!orderId ? +orderId : 0;
        const orderNumber = data['orderNumber'];
        this.orderNumber = orderNumber || null;
        const modelId = data['modelId'];
        this.modelId = !!modelId ? +modelId : 0;
        this.orders.search = '';
        if (!this.orderId || !this.orderNumber) {
          this.orderId = 0;
          this.orderNumber = '';
          this.getOrders(0);
        } else {
          this.orders.data = [];
        }
      });
  }
}
